/**
 * Utility functions for generating and managing URL slugs
 */

/**
 * Convert text to URL-friendly slug
 * @param {string} text - The text to slugify
 * @returns {string} - The slugified text
 */
export function slugify(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  return text
    .toLowerCase()
    .trim()
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove special characters except hyphens and alphanumeric
    .replace(/[^a-z0-9-]/g, '')
    // Replace multiple consecutive hyphens with single hyphen
    .replace(/-+/g, '-')
    // Remove leading and trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit length to 100 characters for practical URLs
    .substring(0, 100);
}

/**
 * Generate a slug from chat title, with fallback options
 * @param {string} title - The chat title
 * @param {Array} messages - Chat messages for fallback content
 * @param {string} fallback - Default fallback text
 * @returns {string} - Generated slug
 */
export function generateChatSlug(title, messages = [], fallback = 'new-chat') {
  // Try the title first
  if (title && title.trim()) {
    const titleSlug = slugify(title);
    if (titleSlug) {
      return titleSlug;
    }
  }
  
  // Fallback to first user message content
  if (Array.isArray(messages) && messages.length > 0) {
    const firstUserMessage = messages.find(m => m.role === 'user' && m.content);
    if (firstUserMessage && firstUserMessage.content) {
      const contentSlug = slugify(firstUserMessage.content);
      if (contentSlug) {
        return contentSlug;
      }
    }
  }
  
  // Final fallback
  return fallback;
}

/**
 * Check if a slug matches the canonical slug for a chat
 * @param {string} currentSlug - The slug from the URL
 * @param {string} canonicalSlug - The canonical slug from the document
 * @returns {boolean} - Whether the slugs match
 */
export function isSlugCanonical(currentSlug, canonicalSlug) {
  if (!currentSlug && !canonicalSlug) {
    return true; // Both empty is considered a match
  }
  
  return currentSlug === canonicalSlug;
}

/**
 * Extract chatId and slug from URL pathname
 * @param {string} pathname - The URL pathname (e.g., "/chat/123/master-bath-remodel")
 * @returns {object} - Object with chatId and slug
 */
export function parseChatlocation(pathname) {
  if (!pathname || typeof pathname !== 'string') {
    return { chatId: null, slug: null };
  }
  
  const pathParts = pathname.split('/').filter(Boolean);
  
  if (pathParts[0] !== 'chat') {
    return { chatId: null, slug: null };
  }
  
  return {
    chatId: pathParts[1] || null,
    slug: pathParts[2] || null
  };
}

/**
 * Path Stripe should return to after dispatch checkout (success or cancel).
 * Prefers the current /chat URL so slug and client thread ids are preserved.
 * @param {{ pathname?: string, thread?: { id?: string, serverConversationId?: string, slug?: string } | null }} params
 * @returns {string}
 */
export function buildCheckoutReturnChatPath({ pathname, thread }) {
  const path = String(pathname || "")
    .trim()
    .split("?")[0]
    .split("#")[0];
  const isGenericChatHome = path === "/chat" || path === "/chat/";
  if (
    path.startsWith("/chat") &&
    !isGenericChatHome &&
    !path.includes("://") &&
    !path.includes("..")
  ) {
    return path;
  }
  if (!thread) return "/chat";
  const id = String(thread.serverConversationId || thread.id || "").trim();
  if (!id) return "/chat";
  const slug = String(thread.slug || "").trim();
  if (slug) return `/chat/${id}/${slug}`;
  return `/chat/${id}`;
}